import Button from '@components/ui/button.vue';

import { mapState, mapGetters } from 'vuex';

export default {
  data: function() {
    return {
      vert: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
      `,

      frag: `
      varying vec2 vUv;
      
      uniform sampler2D texture1;
      uniform sampler2D texture2;
      uniform sampler2D disp;
      
      uniform float dispPower;
      uniform float intensity;
      
      uniform vec2 size;
      uniform vec2 res;
      
      vec2 backgroundCoverUv( vec2 screenSize, vec2 imageSize, vec2 uv ) {
        float screenRatio = screenSize.x / screenSize.y;
        float imageRatio = imageSize.x / imageSize.y;
        vec2 newSize = screenRatio < imageRatio 
            ? vec2(imageSize.x * (screenSize.y / imageSize.y), screenSize.y)
            : vec2(screenSize.x, imageSize.y * (screenSize.x / imageSize.x));
        vec2 newOffset = (screenRatio < imageRatio 
            ? vec2((newSize.x - screenSize.x) / 2.0, 0.0) 
            : vec2(0.0, (newSize.y - screenSize.y) / 2.0)) / newSize;
        return uv * screenSize / newSize + newOffset;
      }
      
      void main() {
        vec2 uv = vUv;
        
        vec4 disp = texture2D(disp, uv);
        vec2 dispVec = vec2(disp.x, disp.y);
        
        vec2 distPos1 = uv + (dispVec * intensity * dispPower);
        vec2 distPos2 = uv + (dispVec * -(intensity * (1.0 - dispPower)));
        
        vec4 _texture1 = texture2D(texture1, distPos1);
        vec4 _texture2 = texture2D(texture2, distPos2);
        
        gl_FragColor = mix(_texture1, _texture2, dispPower);
      }
      `,

      el: null,
      inner: null,
      slides: null,

      width: null,
      height: null,

      scene: null,
      clock: null,
      camera: null,

      images: null,
      imageOutScreenPosition: null,

      data: {},

      state: {
        animating: false,
        text: false,
        initial: true,
        start: true,
      },

      prevActiveSlide: false,
      nextActiveSlide: true,

      innerBg: {
        backgroundImage: null,
      },
    };
  },
  components: {
    't-button': Button,
  },
  watch: {
    'data.current': function(newV, oldV) {
      if (newV > 0 && newV < this.data.total) {
        (this.prevActiveSlide = true), (this.nextActiveSlide = true);
      }
      if (newV === 0) {
        (this.prevActiveSlide = false), (this.nextActiveSlide = true);
      }
      if (newV === this.data.total) {
        (this.prevActiveSlide = true), (this.nextActiveSlide = false);
      }
    },
  },
  computed: {
    ...mapState('slider', ['sliderItems']),
    ...mapGetters('slider', ['getBackgroundImage']),
    numberOfSlides: function() {
      return this.data.total + 1;
    },
    currentSlide: function() {
      if (this.data.total) {
        return this.data.current + 1;
      } else 0;
    },
  },
  created() {
    this.innerBg.backgroundImage = 'url(' + this.getBackgroundImage[0] + ')';
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      this.bindAll();

      this.el = document.querySelector('.js-slider');
      this.inner = this.el.querySelector('.js-slider__inner');
      this.slides = [...this.el.querySelectorAll('.js-slide')];
      this.bullets = [...this.el.querySelectorAll('.js-slider-bullet')];

      let ratio = 1920 / 1080;

      if (window.innerWidth < 1280) {
        this.width = this.el.offsetHeight * ratio;
        this.height = this.el.offsetHeight;
        this.imageOutScreenPosition = 400;
      } else {
        this.width = window.innerWidth;
        this.height = this.el.offsetHeight;
        this.imageOutScreenPosition = 200;
      }

      if (this.$refs.sliderInner && window.innerWidth < 1280) {
        this.$refs.sliderInner.style.width = this.width + 'px';
        this.$refs.sliderInner.style.height = this.height + 'px';
      }

      this.images = this.getBackgroundImage;

      this.data = {
        current: 0,
        total: this.images.length - 1,
        delta: 0,
      };

      this.setup();
      this.cameraSetup();
      this.loadTextures();
      this.createMesh();
      this.setStyles();
      this.render();
      this.listeners();
    },

    bindAll() {
      ['render', 'nextSlide', 'prevSlide'].forEach(
        (fn) => (this[fn] = this[fn].bind(this))
      );
    },

    setStyles() {
      this.slides.forEach((slide, index) => {
        if (index === 0) return;

        TweenMax.set(slide, { autoAlpha: 0 });
      });
    },

    cameraSetup() {
      this.camera = new THREE.OrthographicCamera(
        this.width / -2,
        this.width / 2,
        this.height / 2,
        this.height / -2,
        1,
        1000
      );

      this.camera.lookAt(this.scene.position);
      this.camera.position.z = 1;
    },

    setup() {
      this.scene = new THREE.Scene();
      this.clock = new THREE.Clock(true);

      this.renderer = new THREE.WebGLRenderer({ alpha: true });
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.renderer.setSize(this.width, this.height);

      this.inner.appendChild(this.renderer.domElement);
    },

    loadTextures() {
      const loader = new THREE.TextureLoader();
      loader.crossOrigin = '';

      this.textures = [];
      this.images.forEach((image, index) => {
        const texture = loader.load(image + '?v=' + Date.now(), this.render);

        texture.minFilter = THREE.LinearFilter;
        texture.generateMipmaps = false;

        if (index === 0 && this.mat) {
          this.mat.uniforms.size.value = [
            texture.image.naturalWidth,
            texture.image.naturalHeight,
          ];
        }

        this.textures.push(texture);
      });

      this.disp = loader.load(
        '/dist/images/header_slider/overlay.png',
        this.render
      );
      this.disp.magFilter = this.disp.minFilter = THREE.LinearFilter;
      this.disp.wrapS = this.disp.wrapT = THREE.RepeatWrapping;
    },

    createMesh() {
      this.mat = new THREE.ShaderMaterial({
        uniforms: {
          dispPower: { type: 'f', value: 0.0 },
          intensity: { type: 'f', value: 0.5 },
          res: {
            value: new THREE.Vector2(window.innerWidth, window.innerHeight),
          },
          size: { value: new THREE.Vector2(1, 1) },
          texture1: { type: 't', value: this.textures[0] },
          texture2: { type: 't', value: this.textures[1] },
          disp: { type: 't', value: this.disp },
        },
        transparent: true,
        vertexShader: this.vert,
        fragmentShader: this.frag,
      });

      const geometry = new THREE.PlaneBufferGeometry(
        this.width,
        this.height,
        1
      );

      const mesh = new THREE.Mesh(geometry, this.mat);

      this.scene.add(mesh);
    },

    transitionNext() {
      let next = null;
      let current = null;

      if (this.data.current === 0) {
        this.mat.uniforms.texture1.value = this.textures[this.data.total];
        this.mat.uniforms.texture2.value = this.textures[this.data.current];
        next = this.slides[this.data.current];
        current = this.slides[this.data.total];
      } else {
        this.mat.uniforms.texture1.value = this.textures[this.data.current - 1];
        this.mat.uniforms.texture2.value = this.textures[this.data.current];
        next = this.slides[this.data.current];
        current = this.slides[this.data.current - 1];
      }

      TweenMax.to(this.mat.uniforms.dispPower, 2.5, {
        value: 1,
        ease: Expo.easeInOut,
        onUpdate: this.render,
        onComplete: () => {
          this.mat.uniforms.dispPower.value = 0.0;
          this.render.bind(this);
          this.state.animating = false;
        },
      });

      const currentImages = current.querySelectorAll('.js-slide__img');
      const nextImages = next.querySelectorAll('.js-slide__img');

      const currentText = current.querySelectorAll(
        '.js-slider__text-line > div'
      );
      const nextText = next.querySelectorAll('.js-slider__text-line > div');

      const tl = new TimelineMax({ paused: true });

      tl.staggerFromTo(
        currentImages,
        1.5,
        {
          yPercent: 0,
          scale: 1,
        },
        {
          yPercent: -this.imageOutScreenPosition,
          scaleY: 1.5,
          ease: Expo.easeInOut,
        },
        0.075
      );

      if (currentText) {
        tl.fromTo(
          currentText,
          2,
          {
            yPercent: 0,
          },
          {
            yPercent: -100,
            ease: Power4.easeInOut,
          },
          0
        );
      }

      tl.set(current, {
        autoAlpha: 0,
      }).set(
        next,
        {
          autoAlpha: 1,
        },
        1
      );

      if (nextText) {
        tl.fromTo(
          nextText,
          2,
          {
            yPercent: 100,
          },
          {
            yPercent: 0,
            ease: Power4.easeOut,
          },
          1.5
        );
      }

      tl.staggerFromTo(
        nextImages,
        1.5,
        {
          yPercent: this.imageOutScreenPosition,
          scaleY: 1.5,
        },
        {
          yPercent: 0,
          scaleY: 1,
          ease: Expo.easeInOut,
        },
        0.075,
        1
      );

      tl.play();
    },

    transitionPrev() {
      console.log(
        'transitionPrev',
        'current',
        this.data.current,
        'next',
        this.data.current - 1
      );

      let next = null;
      let current = null;

      if (this.data.current === this.data.total) {
        this.mat.uniforms.texture1.value = this.textures[0];
        this.mat.uniforms.texture2.value = this.textures[this.data.current];
        next = this.slides[this.data.current];
        current = this.slides[0];
      } else {
        this.mat.uniforms.texture1.value = this.textures[this.data.current + 1];
        this.mat.uniforms.texture2.value = this.textures[this.data.current];
        next = this.slides[this.data.current];
        current = this.slides[this.data.current + 1];
      }

      TweenMax.to(this.mat.uniforms.dispPower, 2.5, {
        value: 1,
        ease: Expo.easeInOut,
        onUpdate: this.render,
        onComplete: () => {
          this.mat.uniforms.dispPower.value = 0.0;
          this.render.bind(this);
          this.state.animating = false;
        },
      });

      const currentImages = current.querySelectorAll('.js-slide__img');
      const nextImages = next.querySelectorAll('.js-slide__img');

      const currentText = current.querySelectorAll(
        '.js-slider__text-line > div'
      );
      const nextText = next.querySelectorAll('.js-slider__text-line > div');

      const tl = new TimelineMax({ paused: true });

      tl.staggerFromTo(
        currentImages,
        1.5,
        {
          yPercent: 0,
          scale: 1,
        },
        {
          yPercent: this.imageOutScreenPosition,
          scaleY: 1.5,
          ease: Expo.easeInOut,
        },
        0.075
      );

      if (currentText) {
        tl.fromTo(
          currentText,
          2,
          {
            yPercent: 0,
          },
          {
            yPercent: 100,
            ease: Power4.easeInOut,
          },
          0
        );
      }

      tl.set(current, {
        autoAlpha: 0,
      }).set(
        next,
        {
          autoAlpha: 1,
        },
        1
      );

      if (nextText) {
        tl.fromTo(
          nextText,
          2,
          {
            yPercent: -100,
          },
          {
            yPercent: 0,
            ease: Power4.easeOut,
          },
          1.5
        );
      }

      tl.staggerFromTo(
        nextImages,
        1.5,
        {
          yPercent: -this.imageOutScreenPosition,
          scaleY: 1.5,
        },
        {
          yPercent: 0,
          scaleY: 1,
          ease: Expo.easeInOut,
        },
        0.075,
        1
      );

      tl.play();
    },

    prevSlide() {
      if (this.state.animating) return;
      /*
      if (this.data.current > 0) {
        this.state.animating = true;
        this.data.current =
          this.data.current - 1 < 0 ? this.data.total : this.data.current - 1;
        this.data.next =
          this.data.current - 1 < 0 ? this.data.total : this.data.current - 1;

        this.transitionPrev();
      }
      */

      this.state.animating = true;
      this.data.current =
        this.data.current === 0 ? this.data.total : this.data.current - 1;

      this.transitionPrev();
    },

    nextSlide() {
      if (this.state.animating) return;
      /*
      if (this.data.current < this.data.total) {
        this.state.animating = true;
        this.data.current =
          this.data.current === this.data.total ? 0 : this.data.current + 1;
        this.data.next =
          this.data.current === this.data.total ? 0 : this.data.current + 1;

        this.transitionNext();
      }
      */
      this.state.animating = true;
      this.data.current =
        this.data.current === this.data.total ? 0 : this.data.current + 1;

      this.transitionNext();
    },

    listeners() {
      setTimeout(() => {
        try {
          this.el
            .querySelector('.js-slider-arrow-prev')
            .addEventListener('click', this.prevSlide);
          this.el
            .querySelector('.js-slider-arrow-next')
            .addEventListener('click', this.nextSlide);
        } catch (e) {}
      }, 100);
    },

    render() {
      this.renderer.render(this.scene, this.camera);
    },
  },
};



// WEBPACK FOOTER //
// ./src/templates/components/header-slider/script.js